import { inspectYText, YHistoryEditor } from '@slate-yjs/core';
import type {
  DeltaAttributes,
  DeltaDelete,
  DeltaRetain,
  RecursiveDeltaInsert,
  YjsEditor,
} from '@slate-yjs/core';
import type { Editor, Operation } from 'slate';
import { XmlText, YTextEvent } from 'yjs';

import type { DocParts } from '../internal';
import { CursorEditor } from '../internal';

const DEBUG_MODE = false;
const MAX_RECENT_EVENTS = 500;

export interface BaseYjsErrorContext {
  editorChildren: DocParts;
  recentOperations: RecentEvent[];
  sharedRoot: RecursiveDeltaInsert;
}

export interface RecentEvent {
  data: Operation | InspectedYjsEvent;
  stack: string[];
  time: number;
}

export interface InspectedYjsEvent {
  changedKeys: string[];
  delta: Array<Partial<(DeltaRetain & DeltaAttributes) | DeltaDelete | RecursiveDeltaInsert>>;
  path: YTextEvent['path'];
  originType?: string;
}

/**
 * Catch any errors that occur in relation to Yjs. For each error, include additional context that
 * can be sent to Sentry to help investigate the error.
 */
export function withCatchYjsErrors(
  editor: Editor,
  options: {
    onError: (options: { context: unknown; error: Error; isLocal: boolean }) => void;
  }
): Editor {
  const { onError } = options;
  if (!YHistoryEditor.isYHistoryEditor(editor) || !CursorEditor.isCursorEditor(editor))
    return editor;

  const { apply, applyRemoteEvents, normalize, onChange, sendCursorPosition } = editor;

  const recentEvents: RecentEvent[] = [];
  const stack: string[] = [];

  function recordEvent(data: RecentEvent['data']) {
    const event: RecentEvent = { data, stack: stack.slice(), time: Date.now() };
    if (DEBUG_MODE) console.debug(data, event.stack);
    recentEvents.unshift(event);
    if (recentEvents.length > MAX_RECENT_EVENTS) recentEvents.length = MAX_RECENT_EVENTS;
  }

  editor.apply = operation => {
    recordEvent(operation);
    apply(operation);
  };

  editor.sharedRoot.observeDeep(events => {
    for (const event of events) {
      if (event instanceof YTextEvent) {
        const { changes, delta, path, transaction } = event;
        const { origin } = transaction;
        const isUndoRedo = origin === editor.undoManager;
        const isLocal = editor.isLocalOrigin(origin);
        const originType = isUndoRedo ? 'undoRedo' : isLocal ? 'local' : 'remote';
        recordEvent(inspectYEvent({ changes, delta, originType, path }));
      }
    }
  });

  editor.applyRemoteEvents = (events, origin) => {
    try {
      stack.push('applyRemoteEvents');
      applyRemoteEvents(events, origin);
    } catch (error) {
      onError({
        context: {
          ...getBaseContext(editor, recentEvents),
          events: events.map(inspectYEvent),
          prevSharedRoot: editor.prevSharedRoot && inspectYText(editor.prevSharedRoot),
        },
        error,
        isLocal: false,
      });
    } finally {
      stack.pop();
    }
  };

  editor.sendCursorPosition = range => {
    try {
      stack.push('sendCursorPosition');
      sendCursorPosition(range);
    } catch (error) {
      onError({
        context: {
          ...getBaseContext(editor, recentEvents),
          range,
        },
        error,
        isLocal: true,
      });
    } finally {
      stack.pop();
    }
  };

  editor.onChange = options => {
    try {
      stack.push('onChange');
      onChange(options);
    } catch (error) {
      onError({
        context: getBaseContext(editor, recentEvents),
        error,
        isLocal: true,
      });
    } finally {
      stack.pop();
    }
  };

  editor.normalize = options => {
    try {
      stack.push('normalize');
      normalize(options);
    } catch (error) {
      onError({
        context: getBaseContext(editor, recentEvents),
        error,
        isLocal: true,
      });
    } finally {
      stack.pop();
    }
  };

  for (const methodName of ['flushLocalChanges', 'undo', 'redo'] as const) {
    const method = editor[methodName];

    editor[methodName] = (...args) => {
      try {
        stack.push(methodName);
        return method(...args);
      } catch (error) {
        onError({
          context: getBaseContext(editor, recentEvents),
          error,
          isLocal: true,
        });
      } finally {
        stack.pop();
      }
    };
  }

  return editor;
}

function getBaseContext(editor: YjsEditor, recentOperations: RecentEvent[]): BaseYjsErrorContext {
  return {
    editorChildren: editor.children as DocParts,
    recentOperations: recentOperations.toReversed(),
    sharedRoot: inspectYText(editor.sharedRoot),
  };
}

export function inspectYEvent({
  changes,
  delta,
  ...rest
}: Pick<YTextEvent, 'changes' | 'delta' | 'path'> & { originType?: string }): InspectedYjsEvent {
  /**
   * Raw deltas from YTextEvents can contain insertions consisting of mutable Y.XmlText objects.
   * These must be converted to an immutable, serializable data structure immediately when the event
   * occurs. Attempting to convert these retroactively will return the latest value of the Y.XmlText
   * objects, not their value at the time the event occurred.
   *
   * This conversion may incur a small performance penalty. However, this is acceptable since
   * insertions containing Y.XmlText objects only appear when new Slate elements are inserted, not
   * during normal typing.
   */
  const mappedDelta = delta.map((change): InspectedYjsEvent['delta'][number] => {
    if ('insert' in change && typeof change.insert !== 'string') {
      const { insert } = change;
      if (!(insert instanceof XmlText)) throw new Error('Unexpected insertion type');
      return { ...change, insert: inspectYText(insert) };
    }
    return change;
  });

  const changedKeys = Array.from(changes.keys.keys());

  return { changedKeys, delta: mappedDelta, ...rest };
}
